




import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
import {Chart} from 'highcharts-vue';
import Highcharts from 'highcharts';
import boost from 'highcharts/modules/boost';
import {SegmentBarDiagramItem} from '@c/diagrams/interfaces';
import {getArrayData} from '@u/machineWorkingUtils';

boost(Highcharts);

@Component({
  components: {
    highcharts: Chart,
  },
})
export default class SegmentBarDiagram extends Vue {
  //Данные основной серии
  @Prop({required: true, type: Array})
  mainSegments!: SegmentBarDiagramItem[];

  //Данные дополнительной серии
  @Prop({required: false, type: Array})
  additionalSegments?: SegmentBarDiagramItem[];

  //Цвета основной серии
  @Prop({required: true, type: Array})
  mainSegmentsColors!: string[];

  //Цвета дополнительной серии
  @Prop({required: false, type: Array})
  additionalSegmentsColors?: string[];

  //Границы диаграммы по оси X
  @Prop({required: true, type: Object})
  xBounds!: {min: number; max: number};

  //Ключ для обновления размеров диаграммы
  @Prop({required: false, type: Number, default: 0})
  resizedKey!: number;

  @Watch('resizedKey')
  onResizedKeyChanged() {
    this.getChart().reflow();
  }

  //Относительное позиционировани
  seriesY = [0, 0];

  //Пороговое значение масштаба по оси X
  @Prop({type: Number, default: 1000})
  xMinScaleValue!: number;

  //Список отображаемых полигонов
  rects: any[] = [];

  //Исходная область графика
  extremes: any;

  //Цвет для разделительных линий между сериями
  splitLineColor = 'rgba(0,0,0,0)';

  //Объект графика
  getChart() {
    return (this.$refs.chart as any).chart;
  }

  mounted() {
    this.onChartMounted();
  }

  //Объект конфигурации графика
  get chartOptions() {
    const series = [];

    //порядок добавления серий важен для прорисовки
    const mainSegments = getArrayData(
      this.mainSegments,
      this.mainSegmentsColors,
      20,
      (elem: SegmentBarDiagramItem) => elem.mode,
    );

    series.push({
      id: 'main',
      type: 'line',
      color: this.splitLineColor,
      data: mainSegments,
    });

    if (this.additionalSegments && this.additionalSegmentsColors) {
      const additionalSegments = getArrayData(
        this.additionalSegments,
        this.additionalSegmentsColors,
        10,
        (elem: SegmentBarDiagramItem) => elem.mode,
      );
      series.push({
        id: 'additional',
        type: 'line',
        color: this.splitLineColor,
        data: additionalSegments,
      });
    }

    const min = this.xBounds.min;
    const max = this.xBounds.max;

    return {
      credits: {
        enabled: false,
      },
      chart: {
        marginLeft: 0,
        marginRight: 0,
        spacingLeft: 0,
        spacingRight: 0,
        marginTop: 0,
        marginBottom: 0,
        spacingTop: 0,
        spacingBottom: 0,
        events: {
          redraw: this.onRedraw,
        },
        animation: false,
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
      plotOptions: {
        series: {
          turboThreshold: 0,
          boostThreshold: 0,
          states: {
            hover: {
              enabled: false,
              halo: {
                size: 0,
              },
            },
          },
          enableMouseTracking: false,
        },
        line: {
          turboThreshold: 0,
          boostThreshold: 0,
          animation: {
            duration: 0,
          },
          marker: {
            enabled: false,
            radius: 0,
          },
        },
      },
      xAxis: {
        visible: false,
        min,
        max,
        title: {
          text: '',
        },
      },
      yAxis: {
        visible: false,
        min: 0,
        max: 20,
        title: {
          enabled: false,
        },
      },
      tooltip: {
        enabled: false,
      },
      title: {
        text: '',
      },
      legend: {
        enabled: false,
      },
      series,
    };
  }

  //Обработчик перерисовки графика
  onRedraw() {
    //Прорисовываем полигоны
    const chart = this.getChart();
    if (chart) {
      this.drawRects(chart);
    }
  }

  //Обработчик загрузки графика
  onChartMounted() {
    //Сохраняем исходную область видимости
    //Прорисовываем полигоны
    const chart = this.getChart();
    if (chart) {
      this.extremes = chart.xAxis[0].getExtremes();
      this.drawRects(chart);
    }
  }

  //Прорисовка полигонов
  drawRects(chart: any) {
    this.rects.forEach(x => x.destroy());
    this.rects = [];
    const xAxis = chart.xAxis[0],
      yAxis = chart.yAxis[0];
    const extremes = xAxis.getExtremes();

    chart.series.forEach((s: any, sIndex: any) => {
      const extremePoints: any[] = [];
      let lastBeforePoint: any = null;

      //Находим точки в отображаемом интервале
      const sPointsLength = s.points.length;
      s.points.forEach((p: any) => {
        if (p.x >= extremes.min && p.x <= extremes.max) {
          extremePoints.push(p);
        } else {
          if (p.x < extremes.min && (!lastBeforePoint || lastBeforePoint.x <= p.x)) {
            lastBeforePoint = p;
          }
        }
      });

      const extremePointsLength = extremePoints.length;
      if (extremePointsLength > 0) {
        //Прорисовываем полигоны
        const barHeight = yAxis.toPixels(this.seriesY[sIndex]) - yAxis.toPixels(extremePoints[0].y);
        for (let i = 0; i < extremePointsLength - 1; ++i) {
          const p = extremePoints[i];
          if (!p.elem) continue;

          const p1 = extremePoints[i + 1];
          const rect = chart.renderer
            .rect(
              xAxis.toPixels(p.x),
              yAxis.toPixels(p.y),
              xAxis.toPixels(p1.x) - xAxis.toPixels(p.x) + 1,
              barHeight,
              0,
            )
            .attr({
              fill: p.elem.color,
              zIndex: 1,
            })
            .add();
          this.rects.push(rect);
        }

        //Прорисовываем полигон от границы интервала до первой точки
        if (lastBeforePoint && lastBeforePoint.elem) {
          const p = extremePoints[0];
          const firstRect = chart.renderer
            .rect(
              xAxis.toPixels(extremes.min),
              yAxis.toPixels(p.y),
              xAxis.toPixels(p.x) - xAxis.toPixels(extremes.min) + 1,
              barHeight,
              0,
            )
            .attr({
              fill: lastBeforePoint.elem.color,
              zIndex: 1,
            })
            .add();
          this.rects.push(firstRect);
        }

        //Прорисовываем полигон от последней точки до границы интервала
        const pl = extremePoints[extremePointsLength - 1];
        if (pl.index != s.points[sPointsLength - 1].index && pl.elem) {
          const lastRect = chart.renderer
            .rect(
              xAxis.toPixels(pl.x),
              yAxis.toPixels(pl.y),
              xAxis.toPixels(extremes.max) - xAxis.toPixels(pl.x) + 1,
              barHeight,
              0,
            )
            .attr({
              fill: pl.elem.color,
              zIndex: 1,
            })
            .add();
          this.rects.push(lastRect);
        }
      } else if (lastBeforePoint && lastBeforePoint.elem) {
        //Если в текущем интервале нет точек, то прорисовываем один полигон между границами
        const barHeight = yAxis.toPixels(this.seriesY[sIndex]) - yAxis.toPixels(lastBeforePoint.y);
        const rect = chart.renderer
          .rect(
            xAxis.toPixels(extremes.min),
            yAxis.toPixels(lastBeforePoint.y),
            xAxis.toPixels(extremes.max) - xAxis.toPixels(extremes.min) + 1,
            barHeight,
            0,
          )
          .attr({
            fill: lastBeforePoint.elem.color,
            zIndex: 1,
          })
          .add();
        this.rects.push(rect);
      }
    });
  }
}
